/**
 * AudioManager - Manages all audio playback using Howler.js
 * Handles music, SFX, spatial audio, and volume controls
 */

import { Howl, Howler } from 'howler';

// Audio configuration types
interface AudioConfig {
  src: string | string[];
  volume?: number;
  loop?: boolean;
  sprite?: { [key: string]: [number, number] };
  rate?: number;
  pool?: number;
}

interface SpatialConfig {
  position: [number, number, number];
  pannerAttr?: {
    panningModel?: 'equalpower' | 'HRTF';
    refDistance?: number;
    rolloffFactor?: number;
    distanceModel?: 'linear' | 'inverse';
    maxDistance?: number;
    coneInnerAngle?: number;
    coneOuterAngle?: number;
    coneOuterGain?: number;
  };
}

interface VolumeSettings {
  master: number;
  music: number;
  sfx: number;
}

export class AudioManager {
  // Audio track storage
  private musicTracks: Map<string, Howl> = new Map();
  private sfxPool: Map<string, Howl> = new Map();
  private currentMusicTrack: string | null = null;
  private currentMusicId: number | null = null;

  // Volume settings (0.0 - 1.0)
  private volumes: VolumeSettings = {
    master: 1.0,
    music: 0.8,
    sfx: 1.0,
  };

  // Fade settings
  private defaultFadeDuration: number = 1000; // milliseconds

  // Listener position (synced with player/camera)
  private listenerPosition: [number, number, number] = [0, 0, 0];
  private listenerOrientation: [number, number, number, number, number, number] = [
    0, 0, -1, 0, 1, 0,
  ]; // forward + up vectors

  // State
  private isInitialized: boolean = false;
  private isMuted: boolean = false;

  // Audio ducking state
  private duckTarget: number = 1.0;     // target ratio during duck (e.g. 0.25)
  private duckRestored: number = 1.0;   // ratio to restore to after duck
  private duckTimer: number | null = null;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize the audio system
   */
  private initialize(): void {
    if (this.isInitialized) {
      console.warn('AudioManager: Already initialized');
      return;
    }

    console.log('AudioManager: Initializing Howler.js audio system');

    // Set initial master volume
    Howler.volume(this.volumes.master);

    // Enable spatial audio globally
    Howler.autoUnlock = true;

    this.isInitialized = true;
    console.log('AudioManager: Initialized successfully');
  }

  // ============================================
  // MUSIC MANAGEMENT
  // ============================================

  /**
   * Load a music track
   */
  public loadMusic(trackName: string, config: AudioConfig): void {
    if (this.musicTracks.has(trackName)) {
      console.warn(`AudioManager: Music track "${trackName}" already loaded`);
      return;
    }

    const howl = new Howl({
      src: config.src,
      volume: config.volume ?? this.volumes.music,
      loop: config.loop ?? true, // Music typically loops
      html5: true, // Use HTML5 Audio for streaming large files
      preload: true,
      onload: () => {
        console.log(`AudioManager: Music track "${trackName}" loaded`);
      },
      onloaderror: (id, error) => {
        console.error(`AudioManager: Failed to load music "${trackName}":`, error);
      },
      onplayerror: (id, error) => {
        console.error(`AudioManager: Playback error for music "${trackName}":`, error);
        // Attempt to unlock audio on mobile/browser restrictions
        howl.once('unlock', () => {
          howl.play();
        });
      },
    });

    this.musicTracks.set(trackName, howl);
  }

  /**
   * Play a music track with optional crossfade
   */
  public playMusic(trackName: string, fadeIn: boolean = true): void {
    const track = this.musicTracks.get(trackName);
    if (!track) {
      console.error(`AudioManager: Music track "${trackName}" not loaded`);
      return;
    }

    // Stop current music if playing
    if (this.currentMusicTrack && this.currentMusicTrack !== trackName) {
      this.stopMusic(true); // Fade out current track
    }

    // Play new track
    const soundId = track.play();
    this.currentMusicId = soundId;
    this.currentMusicTrack = trackName;

    // Apply volume with master and music volume
    const finalVolume = this.volumes.master * this.volumes.music;

    if (fadeIn) {
      track.fade(0, finalVolume, this.defaultFadeDuration, soundId);
    } else {
      track.volume(finalVolume, soundId);
    }

    console.log(`AudioManager: Playing music "${trackName}"`);
  }

  /**
   * Stop the current music track
   */
  public stopMusic(fadeOut: boolean = true): void {
    if (!this.currentMusicTrack || this.currentMusicId === null) {
      return;
    }

    const track = this.musicTracks.get(this.currentMusicTrack);
    if (!track) {
      return;
    }

    if (fadeOut) {
      // Capture ID and track by value — this.currentMusicId may change before the timeout fires
      const idToStop = this.currentMusicId;
      const trackToStop = track;
      this.currentMusicId = null;
      this.currentMusicTrack = null;
      trackToStop.fade(trackToStop.volume(), 0, this.defaultFadeDuration, idToStop);
      setTimeout(() => {
        trackToStop.stop(idToStop);
      }, this.defaultFadeDuration);
    } else {
      track.stop(this.currentMusicId);
      this.currentMusicId = null;
      this.currentMusicTrack = null;
    }

    console.log('AudioManager: Stopped music');
  }

  /**
   * Pause the current music track
   */
  public pauseMusic(): void {
    if (!this.currentMusicTrack || this.currentMusicId === null) {
      return;
    }

    const track = this.musicTracks.get(this.currentMusicTrack);
    if (track) {
      track.pause(this.currentMusicId);
      console.log('AudioManager: Paused music');
    }
  }

  /**
   * Resume the paused music track
   */
  public resumeMusic(): void {
    if (!this.currentMusicTrack || this.currentMusicId === null) {
      return;
    }

    const track = this.musicTracks.get(this.currentMusicTrack);
    if (track) {
      track.play(this.currentMusicId);
      console.log('AudioManager: Resumed music');
    }
  }

  // ============================================
  // SOUND EFFECTS (SFX) MANAGEMENT
  // ============================================

  /**
   * Load a sound effect with pooling
   */
  public loadSFX(sfxName: string, config: AudioConfig): void {
    if (this.sfxPool.has(sfxName)) {
      console.warn(`AudioManager: SFX "${sfxName}" already loaded`);
      return;
    }

    const howl = new Howl({
      src: config.src,
      volume: config.volume ?? this.volumes.sfx,
      loop: config.loop ?? false, // SFX typically don't loop
      sprite: config.sprite,
      pool: config.pool ?? 5, // Pool size for simultaneous playback
      html5: false, // Use Web Audio API for SFX (better for short sounds)
      preload: true,
      onload: () => {
        console.log(`AudioManager: SFX "${sfxName}" loaded`);
      },
      onloaderror: (id, error) => {
        console.error(`AudioManager: Failed to load SFX "${sfxName}":`, error);
      },
    });

    this.sfxPool.set(sfxName, howl);
  }

  /**
   * Play a sound effect (2D - no spatial audio)
   */
  public playSFX(sfxName: string, spriteName?: string): number | null {
    const sfx = this.sfxPool.get(sfxName);
    if (!sfx) {
      // Silently fail if SFX not loaded (sound files not added yet)
      return null;
    }

    // Play sprite or entire sound
    const soundId = spriteName ? sfx.play(spriteName) : sfx.play();

    // Apply volume with master and SFX volume
    const finalVolume = this.volumes.master * this.volumes.sfx;
    sfx.volume(finalVolume, soundId);

    return soundId;
  }

  /**
   * Play a sound effect with 3D spatial audio
   */
  public playSpatialSFX(
    sfxName: string,
    spatialConfig: SpatialConfig,
    spriteName?: string
  ): number | null {
    const sfx = this.sfxPool.get(sfxName);
    if (!sfx) {
      console.error(`AudioManager: SFX "${sfxName}" not loaded`);
      return null;
    }

    // Play sprite or entire sound
    const soundId = spriteName ? sfx.play(spriteName) : sfx.play();

    // Apply spatial audio settings
    sfx.pos(spatialConfig.position[0], spatialConfig.position[1], spatialConfig.position[2], soundId);

    // Apply panner attributes if provided
    if (spatialConfig.pannerAttr) {
      sfx.pannerAttr(spatialConfig.pannerAttr, soundId);
    }

    // Apply volume with master and SFX volume
    const finalVolume = this.volumes.master * this.volumes.sfx;
    sfx.volume(finalVolume, soundId);

    return soundId;
  }

  /** Set the volume of a specific playing SFX instance by sound ID */
  public setSFXInstanceVolume(sfxName: string, soundId: number, volume: number): void {
    const sfx = this.sfxPool.get(sfxName);
    if (sfx) sfx.volume(Math.max(0, Math.min(1, volume)), soundId);
  }

  /**
   * Stop a specific sound effect instance
   */
  public stopSFX(sfxName: string, soundId?: number): void {
    const sfx = this.sfxPool.get(sfxName);
    if (!sfx) {
      console.error(`AudioManager: SFX "${sfxName}" not loaded`);
      return;
    }

    if (soundId !== undefined) {
      sfx.stop(soundId);
    } else {
      sfx.stop(); // Stop all instances
    }
  }

  // ============================================
  // SPATIAL AUDIO LISTENER
  // ============================================

  /**
   * Update the listener position (typically synced with player/camera)
   */
  public updateListenerPosition(x: number, y: number, z: number): void {
    this.listenerPosition = [x, y, z];
    Howler.pos(x, y, z);
  }

  /**
   * Update the listener orientation
   * @param forward - Forward vector [x, y, z]
   * @param up - Up vector [x, y, z]
   */
  public updateListenerOrientation(
    forward: [number, number, number],
    up: [number, number, number]
  ): void {
    this.listenerOrientation = [...forward, ...up];
    Howler.orientation(forward[0], forward[1], forward[2], up[0], up[1], up[2]);
  }

  // ============================================
  // VOLUME CONTROLS
  // ============================================

  /**
   * Set master volume (affects all audio)
   */
  public setMasterVolume(volume: number): void {
    this.volumes.master = Math.max(0, Math.min(1, volume)); // Clamp 0-1
    Howler.volume(this.volumes.master);
    console.log(`AudioManager: Master volume set to ${this.volumes.master}`);
  }

  /**
   * Set music volume
   */
  public setMusicVolume(volume: number): void {
    this.volumes.music = Math.max(0, Math.min(1, volume)); // Clamp 0-1

    // Update current music track volume if playing
    if (this.currentMusicTrack && this.currentMusicId !== null) {
      const track = this.musicTracks.get(this.currentMusicTrack);
      if (track) {
        const finalVolume = this.volumes.master * this.volumes.music;
        track.volume(finalVolume, this.currentMusicId);
      }
    }

    console.log(`AudioManager: Music volume set to ${this.volumes.music}`);
  }

  /**
   * Set SFX volume
   */
  public setSFXVolume(volume: number): void {
    this.volumes.sfx = Math.max(0, Math.min(1, volume)); // Clamp 0-1
    console.log(`AudioManager: SFX volume set to ${this.volumes.sfx}`);
  }

  /**
   * Get current volume settings
   */
  public getVolumes(): VolumeSettings {
    return { ...this.volumes };
  }

  // ============================================
  // MUTE / UNMUTE
  // ============================================

  /**
   * Mute all audio
   */
  public mute(): void {
    Howler.mute(true);
    this.isMuted = true;
    console.log('AudioManager: Muted all audio');
  }

  /**
   * Unmute all audio
   */
  public unmute(): void {
    Howler.mute(false);
    this.isMuted = false;
    console.log('AudioManager: Unmuted all audio');
  }

  /**
   * Toggle mute state
   */
  public toggleMute(): void {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  /**
   * Check if audio is muted
   */
  public isMutedState(): boolean {
    return this.isMuted;
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Unload a music track to free memory
   */
  public unloadMusic(trackName: string): void {
    const track = this.musicTracks.get(trackName);
    if (!track) {
      console.warn(`AudioManager: Music track "${trackName}" not loaded`);
      return;
    }

    // Stop if currently playing
    if (this.currentMusicTrack === trackName) {
      this.stopMusic(false);
    }

    track.unload();
    this.musicTracks.delete(trackName);
    console.log(`AudioManager: Unloaded music "${trackName}"`);
  }

  /**
   * Unload a sound effect to free memory
   */
  public unloadSFX(sfxName: string): void {
    const sfx = this.sfxPool.get(sfxName);
    if (!sfx) {
      console.warn(`AudioManager: SFX "${sfxName}" not loaded`);
      return;
    }

    sfx.unload();
    this.sfxPool.delete(sfxName);
    console.log(`AudioManager: Unloaded SFX "${sfxName}"`);
  }

  /**
   * Unload all audio to free memory
   */
  public unloadAll(): void {
    // Stop current music
    this.stopMusic(false);

    // Unload all music tracks
    this.musicTracks.forEach((track) => track.unload());
    this.musicTracks.clear();

    // Unload all SFX
    this.sfxPool.forEach((sfx) => sfx.unload());
    this.sfxPool.clear();

    console.log('AudioManager: Unloaded all audio');
  }

  /**
   * Stop all currently playing audio
   */
  public stopAll(): void {
    Howler.stop();
    this.currentMusicId = null;
    this.currentMusicTrack = null;
    console.log('AudioManager: Stopped all audio');
  }

  /**
   * Immediately kill ALL audio - no fade, no delay
   * Use this when quitting the game
   */
  public killAllAudio(): void {
    // Mute first for instant silence
    Howler.mute(true);
    // Set global volume to 0
    Howler.volume(0);
    // Stop everything
    Howler.stop();
    // Unload everything
    Howler.unload();
    // Reset state
    this.currentMusicId = null;
    this.currentMusicTrack = null;
    this.musicTracks.clear();
    this.sfxPool.clear();
    this.isInitialized = false;
    console.log('AudioManager: Killed all audio immediately');
  }

  /**
   * Clean up and destroy the audio manager
   */
  public destroy(): void {
    this.stopAll();
    this.unloadAll();
    this.isInitialized = false;
    console.log('AudioManager: Destroyed');
  }

  /**
   * Get the current music track name
   */
  public getCurrentMusicTrack(): string | null {
    return this.currentMusicTrack;
  }

  /**
   * Check if audio system is initialized
   */
  public isReady(): boolean {
    return this.isInitialized;
  }

  // ============================================
  // AUDIO DUCKING
  // ============================================

  /**
   * Temporarily lower music volume (duck) for durationMs, then restore.
   * @param targetRatio  Fraction of current music volume to duck to (e.g. 0.2 = 20%)
   * @param durationMs   How long to stay ducked before fading back
   * @param fadeMs       Fade duration for the duck and restore transitions
   */
  public duckMusic(targetRatio: number = 0.2, durationMs: number = 800, fadeMs: number = 150): void {
    if (!this.currentMusicTrack || this.currentMusicId === null) return;
    const track = this.musicTracks.get(this.currentMusicTrack);
    if (!track) return;

    const fullVolume = this.volumes.master * this.volumes.music;
    const duckedVolume = fullVolume * targetRatio;

    // Cancel any pending restore
    if (this.duckTimer !== null) {
      clearTimeout(this.duckTimer);
      this.duckTimer = null;
    }

    // Fade down
    track.fade(track.volume(this.currentMusicId) as number, duckedVolume, fadeMs, this.currentMusicId);

    // Schedule restore
    this.duckTimer = window.setTimeout(() => {
      if (this.currentMusicTrack && this.currentMusicId !== null) {
        const t = this.musicTracks.get(this.currentMusicTrack);
        if (t) t.fade(t.volume(this.currentMusicId) as number, fullVolume, fadeMs * 2, this.currentMusicId);
      }
      this.duckTimer = null;
    }, durationMs);
  }
}

// Export singleton instance
export const audioManager = new AudioManager();
